import { createContext, useContext, type ReactNode } from 'react'

import { useParams } from 'react-router-dom'

import { resolvePeachIdLink } from './resolve'
import type { PeachIdTable } from './types'

const TableContext = createContext<PeachIdTable | null>(null)

/**
 * Supplies the route table. Absent this provider every value renders as plain
 * text, which is what lets a consumer of these components adopt them without
 * inheriting dev-tools URLs.
 */
export const PeachIdLinkProvider = ({
  table,
  children,
}: {
  table: PeachIdTable
  children: ReactNode
}) => <TableContext.Provider value={table}>{children}</TableContext.Provider>

/**
 * Reads the table without touching a router hook, so a caller can check for a
 * provider before it mounts anything that reads route params.
 */
export const usePeachIdTable = (): PeachIdTable | null =>
  useContext(TableContext)

export const usePeachIdLink = (
  value: unknown,
  {
    fieldName,
    record,
  }: { fieldName?: string; record: { [key: string]: unknown } },
): string | null => {
  const table = useContext(TableContext)
  const params = useParams<{ [key: string]: string }>()

  if (!table) return null
  return resolvePeachIdLink(table, value, { fieldName, params, record })
}
